import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCostLogErrors, type AICostLogError } from "@/services/admin-api";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Button } from "@/components/ui/button";
import { VirtualAdminTable, type VirtualColumnDef } from "@/components/ui/virtual-table";

export default function AICostLogErrors() {
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const { data, isLoading } = useQuery({
    queryKey: ["ai-cost-log-errors", page],
    queryFn: () => getCostLogErrors(page, pageSize),
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return <div className="p-8">Loading...</div>;
  }

  const errors = data?.data ?? [];
  const total = data?.total ?? 0;

  const errorColumns: VirtualColumnDef[] = [
    { key: "timestamp", header: "Timestamp" },
    { key: "promptId", header: "Prompt ID" },
    { key: "model", header: "Model" },
    { key: "user", header: "User" },
    { key: "error", header: "Error" },
  ];

  const renderErrorRow = (err: AICostLogError) => (
    <>
      <div role="cell" className="p-4 align-middle whitespace-nowrap text-muted-foreground">
        {new Date(err.createdAt).toLocaleString()}
      </div>
      <div role="cell" className="p-4 align-middle font-mono text-xs">
        {err.promptId ?? "\u2014"}
      </div>
      <div role="cell" className="p-4 align-middle font-mono text-xs">
        {err.model ?? "\u2014"}
      </div>
      <div role="cell" className="p-4 align-middle text-xs text-muted-foreground">
        {err.userId ?? "\u2014"}
      </div>
      <div
        role="cell"
        className="p-4 align-middle max-w-xs truncate text-xs text-red-700"
        title={err.error}
      >
        {err.error}
      </div>
    </>
  );

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="AI Cost Log Failures"
        description="AI calls that could not be logged to ai_executions. An empty list means all cost logging is healthy."
      />

      {total === 0 ? (
        <div className="rounded-lg border border-green-200 bg-green-50 p-6 text-green-800">
          No cost logging failures detected. All AI calls are being logged correctly.
        </div>
      ) : (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          <strong>{total} failed log entries</strong> — these AI calls were not recorded in cost
          tracking.
        </div>
      )}

      {errors.length > 0 && (
        <>
          <VirtualAdminTable data={errors} columns={errorColumns} renderRow={renderErrorRow} />

          {total > pageSize && (
            <div className="flex items-center justify-between border-t p-4">
              <span className="text-sm text-muted-foreground">
                Showing {(page - 1) * pageSize + 1}
                {"\u2013"}
                {Math.min(page * pageSize, total)} of {total}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page * pageSize >= total}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
